import type {
  ExpenseClaimFinanceState,
  ExpenseClaimStatus,
  FinancePayableSyncView,
  FinanceStatus,
  PayableStatus,
  PaymentStatus,
} from './types/reimbursement';

export const FINANCE_STATUSES: FinanceStatus[] = [
  'NOT_STARTED',
  'SYNC_PENDING',
  'SYNC_FAILED',
  'PAYABLE_CREATED',
  'PENDING_PAYMENT',
  'PAYING',
  'PAYMENT_FAILED',
  'PAID',
  'CANCELED',
];

export const PAYABLE_STATUSES: PayableStatus[] = [
  'CREATED',
  'PENDING_PAYMENT',
  'PARTIALLY_PAID',
  'PAID',
  'CANCELED',
];

export const PAYMENT_STATUSES: PaymentStatus[] = [
  'NOT_STARTED',
  'PENDING',
  'PROCESSING',
  'FAILED',
  'SUCCEEDED',
  'CANCELED',
];

const PAYABLE_ESTABLISHED_STATUSES: FinanceStatus[] = [
  'PAYABLE_CREATED',
  'PENDING_PAYMENT',
  'PAYING',
  'PAYMENT_FAILED',
  'PAID',
  'CANCELED',
];

const LEGACY_CONFLICT_CODE = 'CONFLICT';

export function isFinanceStatus(value: unknown): value is FinanceStatus {
  return (
    typeof value === 'string' &&
    (FINANCE_STATUSES as string[]).includes(value)
  );
}

export function isPayableStatus(value: unknown): value is PayableStatus {
  return (
    typeof value === 'string' &&
    (PAYABLE_STATUSES as string[]).includes(value)
  );
}

export function isPaymentStatus(value: unknown): value is PaymentStatus {
  return (
    typeof value === 'string' &&
    (PAYMENT_STATUSES as string[]).includes(value)
  );
}

function asNullableString(value: unknown): string | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function fromSnapshot(data: Record<string, unknown>): ExpenseClaimFinanceState {
  return {
    financePayableUuid: asNullableString(data.financePayableUuid),
    financePayableNo: asNullableString(data.financePayableNo),
    financeStatus: data.financeStatus as FinanceStatus,
    payableStatus: isPayableStatus(data.payableStatus)
      ? data.payableStatus
      : null,
    paymentStatus: isPaymentStatus(data.paymentStatus)
      ? data.paymentStatus
      : null,
    payableAmount: asNullableString(data.payableAmount),
    paidAmount: asNullableString(data.paidAmount),
    currency: asNullableString(data.currency),
    paidAt: asNullableString(data.paidAt),
    paymentReference: asNullableString(data.paymentReference),
    financeUpdatedAt: asNullableString(data.financeUpdatedAt),
    lastSyncedAt: asNullableString(data.lastSyncedAt),
    lastErrorCode: asNullableString(data.lastErrorCode),
    lastErrorMessage: asNullableString(data.lastErrorMessage),
  };
}

function fromLegacySync(
  data: FinancePayableSyncView,
): ExpenseClaimFinanceState {
  const financePayableUuid = asNullableString(data.financePayableUuid);
  let financeStatus: FinanceStatus = 'NOT_STARTED';
  let lastErrorCode: string | null = null;
  if (data.status === 'PENDING') {
    financeStatus = 'SYNC_PENDING';
  } else if (data.status === 'FAILED') {
    financeStatus = 'SYNC_FAILED';
  } else if (data.status === 'SUCCESS') {
    financeStatus = financePayableUuid ? 'PAYABLE_CREATED' : 'SYNC_PENDING';
  } else if (data.status === 'CONFLICT') {
    financeStatus = 'SYNC_FAILED';
    lastErrorCode = LEGACY_CONFLICT_CODE;
  }
  return {
    financePayableUuid,
    financePayableNo: null,
    financeStatus,
    payableStatus: null,
    paymentStatus: null,
    payableAmount: null,
    paidAmount: null,
    currency: null,
    paidAt: null,
    paymentReference: null,
    financeUpdatedAt: asNullableString(data.lastAttemptAt),
    lastSyncedAt: asNullableString(data.syncedAt),
    lastErrorCode,
    lastErrorMessage: asNullableString(data.lastError),
  };
}

export function normalizeExpenseClaimFinanceState(
  raw: unknown,
): ExpenseClaimFinanceState | null {
  if (raw == null) {
    return null;
  }
  if (!isRecord(raw)) {
    return null;
  }
  if (isFinanceStatus(raw.financeStatus)) {
    return fromSnapshot(raw);
  }
  if (
    raw.status === 'PENDING' ||
    raw.status === 'SUCCESS' ||
    raw.status === 'FAILED' ||
    raw.status === 'CONFLICT'
  ) {
    return fromLegacySync(raw as unknown as FinancePayableSyncView);
  }
  return null;
}

export function isApprovalPassed(status?: ExpenseClaimStatus | null) {
  return status === 'approved' || status === 'completed';
}

export function isPayableEstablished(
  finance?: ExpenseClaimFinanceState | null,
) {
  if (!finance) {
    return false;
  }
  if (finance.financePayableUuid) {
    return true;
  }
  return PAYABLE_ESTABLISHED_STATUSES.includes(finance.financeStatus);
}

export function isLegacyFinanceConflict(
  finance?: ExpenseClaimFinanceState | null,
) {
  return finance?.lastErrorCode === LEGACY_CONFLICT_CODE;
}

export function canCreateFinancePayable(
  status?: ExpenseClaimStatus | null,
  finance?: ExpenseClaimFinanceState | null,
) {
  if (!isApprovalPassed(status)) {
    return false;
  }
  if (finance?.financeStatus !== 'SYNC_FAILED') {
    return false;
  }
  if (isPayableEstablished(finance)) {
    return false;
  }
  return true;
}

export function canSyncFinanceStatus(
  status?: ExpenseClaimStatus | null,
  finance?: ExpenseClaimFinanceState | null,
) {
  if (!isApprovalPassed(status)) {
    return false;
  }
  return isPayableEstablished(finance);
}

export function getFinanceStatusLabel(
  finance?: ExpenseClaimFinanceState | null,
) {
  if (!finance) {
    return '尚未进入财务处理';
  }
  if (finance.payableStatus === 'PARTIALLY_PAID') {
    return '部分付款';
  }
  return (
    {
      NOT_STARTED: '尚未进入财务处理',
      SYNC_PENDING: '财务应付创建中',
      SYNC_FAILED: '财务应付创建失败',
      PAYABLE_CREATED: '已生成财务应付',
      PENDING_PAYMENT: '待付款',
      PAYING: '付款处理中',
      PAYMENT_FAILED: '付款失败',
      PAID: '已付款',
      CANCELED: '已取消',
    } satisfies Record<FinanceStatus, string>
  )[finance.financeStatus];
}

export type ClaimLifecycleStepState = 'done' | 'current' | 'wait' | 'error';

export type ClaimLifecycleStepKey =
  | 'submitted'
  | 'approved'
  | 'payableCreated'
  | 'pendingPayment'
  | 'paying'
  | 'paid'
  | 'completed';

export type ClaimLifecycleStep = {
  key: ClaimLifecycleStepKey;
  label: string;
  state: ClaimLifecycleStepState;
};

export function buildClaimLifecycle(
  status?: ExpenseClaimStatus | null,
  finance?: ExpenseClaimFinanceState | null,
): ClaimLifecycleStep[] {
  const submitted =
    status === 'waiting_workflow' ||
    status === 'submitted' ||
    status === 'approved' ||
    status === 'completed' ||
    status === 'rejected' ||
    status === 'terminated' ||
    status === 'withdrawn';
  const approved = isApprovalPassed(status);
  const completed = status === 'completed';
  const financeStatus = finance?.financeStatus;
  const partial = finance?.payableStatus === 'PARTIALLY_PAID';
  const payableDone =
    isPayableEstablished(finance) && financeStatus !== 'SYNC_FAILED';
  const paid = financeStatus === 'PAID';
  const canceled = financeStatus === 'CANCELED';
  const paymentFailed = financeStatus === 'PAYMENT_FAILED';
  const paying = financeStatus === 'PAYING';
  const waitingPayment =
    financeStatus === 'PAYABLE_CREATED' ||
    financeStatus === 'PENDING_PAYMENT' ||
    paymentFailed ||
    partial;

  const steps: ClaimLifecycleStep[] = [
    {
      key: 'submitted',
      label: '提交报销',
      state: submitted ? 'done' : 'wait',
    },
    {
      key: 'approved',
      label: '审批通过',
      state: approved
        ? 'done'
        : submitted && status === 'submitted'
          ? 'current'
          : 'wait',
    },
    {
      key: 'payableCreated',
      label: '已生成财务应付',
      state: !approved
        ? 'wait'
        : financeStatus === 'SYNC_FAILED'
          ? 'error'
          : financeStatus === 'SYNC_PENDING'
            ? 'current'
            : payableDone
              ? 'done'
              : approved
                ? 'current'
                : 'wait',
    },
    {
      key: 'pendingPayment',
      label: '待付款',
      state: !payableDone || canceled
        ? 'wait'
        : paid || paying
          ? 'done'
          : waitingPayment
            ? 'current'
            : 'wait',
    },
    {
      key: 'paying',
      label: '付款处理中',
      state: !payableDone || canceled
        ? 'wait'
        : paid
          ? 'done'
          : paying
            ? 'current'
            : paymentFailed
              ? 'error'
              : 'wait',
    },
    {
      key: 'paid',
      label: '已付款',
      state: paid ? 'done' : 'wait',
    },
    {
      key: 'completed',
      label: '报销完成',
      state: completed ? 'done' : 'wait',
    },
  ];

  return steps;
}
